/* eslint-disable complexity */
import { useNavigate } from 'react-router-dom';
import Typography from 'carbon-react/lib/components/typography';
import Password from 'carbon-react/lib/components/password';
import Button from 'carbon-react/lib/components/button';
import Link from 'carbon-react/lib/components/link';
import classNames from 'classnames';
import React, { FocusEvent, ReactNode, useCallback, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { UISystemIcon } from '@futrli/ui-components';
import { useWindowSize } from '@futrli/shared-services';

import styles from '../../core/common.scss';
import { DESKTOP_LAYOUT_LIMIT, FORGOT_PASSWORD_SUBMIT_FORM_ID, FORM_FIELDS, LOGIN_ROUTE } from '../../core/constants';
import { AuthError } from '../../core/types';
import { getFormValidator } from '../../utils/validation-utils';

interface Props {
  isLoading: boolean;
  forgotPasswordSubmitError: AuthError;
  onSubmit: (password: string) => void;
}

interface PasswordFormData {
  password: string;
  confirmPassword: string;
}

type PasswordTouchedValues = Record<string, boolean>;

type PasswordErrorData = Record<string, ReactNode>;

export const ForgotPasswordSubmitForm = ({ isLoading, forgotPasswordSubmitError, onSubmit }: Props) => {
  const [touchedValues, setTouchedValues] = useState<PasswordTouchedValues>({
    password: false,
    confirmPassword: false
  });

  const [formValues, setFormValues] = useState<PasswordFormData>({
    password: '',
    confirmPassword: ''
  });

  const intl = useIntl();

  const navigate = useNavigate();

  const validate = getFormValidator(
    [FORM_FIELDS.password, FORM_FIELDS.confirmPassword],
    FORGOT_PASSWORD_SUBMIT_FORM_ID,
    intl
  );

  const [errors, setErrors] = useState<PasswordErrorData>(validate((formValues as unknown) as Record<string, string>));

  const { width: windowWidth } = useWindowSize();

  useEffect(() => {
    if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
      document.body.classList.add(styles.bodyStyles);
    }

    return () => {
      if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
        document.body.classList.remove(styles.bodyStyles);
      }
    };
  }, [windowWidth]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormValues({
        ...formValues,
        [name]: value
      });
      setErrors(
        validate({
          ...formValues,
          [name]: value
        })
      );
      if (!touchedValues[name]) {
        setTouchedValues({
          ...touchedValues,
          [name]: true
        });
      }
    },
    [formValues, validate, touchedValues]
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const { name } = e.target;
      if (!touchedValues[name]) {
        setTouchedValues({
          ...touchedValues,
          [name]: true
        });
      }
    },
    [touchedValues]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const disabled = isLoading || Object.keys(errors).length > 0;
    if (e.key === 'Enter' && !disabled) {
      onSubmit(formValues.password);
    }
  };

  const handleLoginClick = () => navigate(LOGIN_ROUTE);

  const handleSubmit = () => onSubmit(formValues.password);

  const dataError = Object.keys(errors).length > 0;

  return (
    <main className={styles.newPageContainer}>
      <div className={classNames(styles.newPageContent, styles.mobileHeight)}>
        <div className={styles.registerFormContainer}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <UISystemIcon iconName="dark-by-sage-logo" width={202} height={90} />
          </div>
          <Typography variant="h1" textAlign="center" marginBottom={40}>
            <FormattedMessage id="change_password.enter_new_password_below" defaultMessage="Enter new password below" />
          </Typography>
          <Password
            label={intl.formatMessage({
              id: 'change_password.new_password',
              defaultMessage: 'New password'
            })}
            placeholder={intl.formatMessage({
              id: 'login.password_placeholder',
              defaultMessage: 'Enter a password (9 characters, special, upper, lower, number)'
            })}
            disabled={isLoading}
            value={formValues.password}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            name={FORM_FIELDS.password}
            error={!!errors.password && touchedValues.password ? (errors.password as string) : ''}
            mb={40}
            size="large"
          />
          <Password
            label={intl.formatMessage({
              id: 'change_password.confirm_new_password',
              defaultMessage: 'Confirm new password'
            })}
            placeholder={intl.formatMessage({
              id: 'login.password_placeholder',
              defaultMessage: 'Enter a password (9 characters, special, upper, lower, number)'
            })}
            disabled={isLoading}
            value={formValues.confirmPassword}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            name={FORM_FIELDS.confirmPassword}
            error={!!errors.confirmPassword && touchedValues.confirmPassword ? (errors.confirmPassword as string) : ''}
            mb={40}
            size="large"
          />
          {forgotPasswordSubmitError && (
            // todo: make sure this message supports i18n
            <Typography fontSize="14px" color="errorRed">
              {forgotPasswordSubmitError.message}
            </Typography>
          )}
          <Button
            fullWidth
            className={styles.newSubmitButton}
            disabled={isLoading || dataError}
            onClick={handleSubmit}
            mb={40}
            buttonType="primary"
            size="large"
          >
            <FormattedMessage id="change_password.save_sign_in" defaultMessage="Save and sign in" />
          </Button>
          <div style={{ display: 'flex', marginBottom: 16 }}>
            <svg width="100%" height="1" viewBox="0 0 400 1" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 0.5H400" stroke="#7F7F7F" strokeWidth="0.5" />
            </svg>
          </div>
          <Link onClick={handleLoginClick} className={styles.sageCarbonAuthLink}>
            <FormattedMessage id="forgot_password.back_to_log_in" defaultMessage="Back to Log in?" />
          </Link>
        </div>
      </div>
    </main>
  );
};
